import React, { ReactNode, useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { UseKeycloakService, KeycloakService } from '@europrocurement/l2d-keycloak';
import {
    CountMessagesBodyResult,
    GetMessagesBodyResult,
    IsConnectedBodyResult,
    MessageItem,
    MessageResponse,
    NewMessageBody,
    WebsocketContext,
    WebsocketPayloadCountMessages,
    WebsocketPayloadDeleteMessage,
    WebsocketPayloadEditMessage,
    WebsocketPayloadIsConnected,
    WebsocketPayloadListMessage,
    WebsocketPayloadMessage,
    WebsocketPayloadSetMessagesRead,
} from './WebsocketContext';
import { useSessionTimeout } from '../hook/useSessionTimeout';
import { sendAndWaitForResponse } from '../utils/websocketUtils';

export type WebsocketProviderType = {
    baseWebsocketUrl: string;
    children: ReactNode;
};

const isTokenValid = (kc: KeycloakService): boolean => {
    const exp = kc.getTokenParsed()?.exp;
    return !!exp && exp * 1000 > Date.now();
};

export const WebsocketProvider: React.FunctionComponent<WebsocketProviderType> = function ({
    baseWebsocketUrl,
    children,
}) {
    const socketRef = useRef<WebSocket | null>(null);
    const kc = UseKeycloakService();
    const keycloakInstance = kc.getKc();
    const [socketOpen, setSocketOpen] = useState<boolean>(false);
    const [messagesByTopic, setMessagesByTopic] = useState<Record<string, MessageItem[]>>({});
    const [unreadCountByTopic, setUnreadCountByTopic] = useState<Record<string, number>>({});
    const [isConnectedByUserIdentifier, setIsConnectedByUserIdentifier] = useState<
        Record<string, boolean>
    >({});
    const [errors, setErrors] = useState<Record<string, unknown>>({});

    const sendOnSocket = useCallback(
        (
            payload:
                | (WebsocketPayloadMessage & { action: string })
                | (WebsocketPayloadEditMessage & { action: string })
                | (WebsocketPayloadDeleteMessage & { action: string })
                | (WebsocketPayloadListMessage & { action: string })
                | (WebsocketPayloadCountMessages & { action: string })
                | (WebsocketPayloadIsConnected & { action: string })
                | (WebsocketPayloadSetMessagesRead & { action: string }),
            actionExpected: string,
        ): Promise<MessageResponse> => {
            const socket = socketRef.current;

            if (!socket || socket.readyState !== WebSocket.OPEN) {
                return Promise.reject(new Error('Socket not open'));
            }
            return sendAndWaitForResponse<MessageResponse>(socket, payload, actionExpected);
        },
        [],
    );

    const sendMessage = useCallback(
        async (payload: WebsocketPayloadMessage): Promise<void> => {
            try {
                await sendOnSocket({ ...payload, action: 'sendmessage' }, 'sendmessage');
            } catch (error) {
                console.error(`[WS] Failed to send a message with payload ${payload}`, error);
            }
        },
        [sendOnSocket],
    );

    const updateMessage = useCallback(
        async (payload: WebsocketPayloadEditMessage): Promise<void> => {
            try {
                await sendOnSocket({ ...payload, action: 'updatemessage' }, 'updatemessage');
            } catch (error) {
                console.error(`[WS] Failed to update a message with payload ${payload}`, error);
            }
        },
        [sendOnSocket],
    );

    const deleteMessage = useCallback(
        async (payload: WebsocketPayloadDeleteMessage): Promise<void> => {
            try {
                await sendOnSocket({ ...payload, action: 'deletemessage' }, 'deletemessage');
            } catch (error) {
                console.error(`[WS] Failed to delete a message with payload ${payload}`, error);
            }
        },
        [sendOnSocket],
    );

    const getIsConnected = useCallback(
        async (userIdentifier: string): Promise<void> => {
            const kcToken = await keycloakInstance
                .updateToken(10)
                .then(() => keycloakInstance.token || '');
            const payload: WebsocketPayloadIsConnected = {
                payload: {
                    token: kcToken,
                    userIdentifier,
                },
            };

            try {
                sendOnSocket({ ...payload, action: 'isconnected' }, 'isconnected');
            } catch (error) {
                console.error(`[WS] Failed to get is connected for ${userIdentifier}`, error);
            }
        },
        [keycloakInstance, sendOnSocket],
    );

    const getMessageCount = useCallback(
        async (topicId: string): Promise<void> => {
            const kcToken = await keycloakInstance
                .updateToken(10)
                .then(() => keycloakInstance.token || '');
            const payload: WebsocketPayloadCountMessages = {
                payload: {
                    token: kcToken,
                    topicId,
                },
            };

            try {
                await sendOnSocket({ ...payload, action: 'countmessage' }, 'countmessages');
            } catch (error) {
                console.error(`[WS] Failed to get count messages for ${topicId}`, error);
            }
        },
        [keycloakInstance, sendOnSocket],
    );

    const setMessagesRead = useCallback(
        async (messagesIds: string[]): Promise<void> => {
            const kcToken = await keycloakInstance
                .updateToken(10)
                .then(() => keycloakInstance.token || '');
            const payload: WebsocketPayloadSetMessagesRead = {
                payload: {
                    token: kcToken,
                    messagesIds,
                },
            };

            try {
                await sendOnSocket({ ...payload, action: 'setmessagesread' }, 'setmessagesread');
            } catch (error) {
                console.error(`[WS] Failed to set messages read for ${messagesIds}`, error);
            }
        },
        [keycloakInstance, sendOnSocket],
    );

    const fetchMessages = useCallback(
        async (topicId: string, from?: number, size?: number): Promise<void> => {
            const kcToken = await keycloakInstance
                .updateToken(10)
                .then(() => keycloakInstance.token || '');
            const userIdentifier = kc.getTokenParsed()?.preferred_username || '';
            let searchParams = {};
            if (from) {
                searchParams = {
                    from,
                };
            }
            if (size) {
                searchParams = {
                    ...searchParams,
                    size,
                };
            }

            const payload: WebsocketPayloadListMessage = {
                payload: {
                    token: kcToken,
                    topicId,
                    userIdentifier,
                    ...searchParams,
                },
            };

            try {
                await sendOnSocket({ ...payload, action: 'getmessages' }, 'getmessages');
            } catch (error) {
                console.error(`[WS] Failed to fetch messages for ${topicId}`, error);
            }
        },
        [kc, keycloakInstance, sendOnSocket],
    );

    const createSocket = useCallback(() => {
        if (socketRef.current || !isTokenValid(kc)) return;

        const userIdentifier = kc.getTokenParsed()?.preferred_username || '';
        const url = `${baseWebsocketUrl}?userIdentifier=${encodeURIComponent(userIdentifier)}`;
        const socket = new WebSocket(url);

        console.debug(`[WS] Creating socket for ${userIdentifier} at ${url}`);
        socket.onopen = () => {
            console.debug(`[WS] Opened at ${url}`);
            setSocketOpen(true);
        };

        socket.onmessage = (event) => {
            console.debug('[WS] - onmessage event : ', event);
            const data: MessageResponse = JSON.parse(event.data);
            try {
                const payload = JSON.parse(data.body);
                console.debug('payload reçu', payload);

                if (payload.action === 'newMessage') {
                    const resultNewMessage: NewMessageBody = payload;
                    setMessagesByTopic((prev) => ({
                        ...prev,
                        [resultNewMessage.message.data.topicId]: [
                            ...(prev[resultNewMessage.message.data.topicId] || []),
                            resultNewMessage.message,
                        ],
                    }));
                }
                if (payload.action === 'updateMesssage') {
                    const resultNewMessage: NewMessageBody = payload;
                    setMessagesByTopic((prev) => {
                        const { topicId } = resultNewMessage.message.data;
                        const currentMessages = prev[topicId] || [];

                        const messageIndex = currentMessages.findIndex(
                            (msg) => msg.messageId === resultNewMessage.message.messageId,
                        );

                        let updatedMessages = [...currentMessages];
                        if (messageIndex !== -1) {
                            updatedMessages = [...currentMessages];
                            updatedMessages[messageIndex] = resultNewMessage.message;
                        }

                        return {
                            ...prev,
                            [topicId]: updatedMessages,
                        };
                    });
                }
                if (payload.action === 'deleteMesssage') {
                    const resultNewMessage: NewMessageBody = payload;
                    setMessagesByTopic((prev) => {
                        const { topicId } = resultNewMessage.message.data;
                        const currentMessages = prev[topicId] || [];

                        const updatedMessages = currentMessages.filter(
                            (msg) => msg.messageId !== resultNewMessage.message.messageId,
                        );

                        return {
                            ...prev,
                            [topicId]: updatedMessages,
                        };
                    });
                }
                if (payload.action === 'getMessages') {
                    const messagesList: GetMessagesBodyResult = payload;
                    const firstTopicId =
                        messagesList.messages.length > 0
                            ? messagesList.messages[0].data.topicId
                            : null;
                    if (firstTopicId) {
                        setMessagesByTopic((prev) => ({
                            ...prev,
                            [firstTopicId]: messagesList.messages,
                        }));
                    }
                }
                if (payload.action === 'getMessagesCount') {
                    const resultCount: CountMessagesBodyResult = payload;
                    setUnreadCountByTopic((prev) => ({
                        ...prev,
                        [resultCount.topicId]: resultCount.count,
                    }));
                }
                if (payload.action === 'isconnected') {
                    const resultIsConnected: IsConnectedBodyResult = payload;
                    setIsConnectedByUserIdentifier((prev) => ({
                        ...prev,
                        [resultIsConnected.userIdentifier]: resultIsConnected.isConnected,
                    }));
                }
            } catch (e) {
                console.error('[WS] Invalid message payload', e);
            }
        };

        socket.onclose = (event) => {
            console.debug('[WS] - onclose event : ', event);
            setSocketOpen(false);
            socketRef.current = null;
            console.debug(`[WS] Closed ${url}`, {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
            });
        };

        socket.onerror = (e) => {
            console.error(`[WS] Error `, e);
            setErrors((prev) => ({
                ...prev,
                e,
            }));
        };

        socketRef.current = socket;
    }, [baseWebsocketUrl, kc]);

    const getSocket = useCallback((): WebSocket | null => socketRef.current ?? null, [socketRef]);

    const hasSocket = useCallback((): boolean => !!socketRef.current, [socketRef]);

    useEffect(() => {
        if (!socketOpen && !socketRef.current) {
            console.debug('Socket fermé, tentative de reconnexion...');
            console.debug('val:', socketOpen, socketRef.current, isTokenValid(kc));
            createSocket();
        }
    }, [socketOpen, socketRef, createSocket, kc]);

    // A réactiver si la ré-ouverture du socket après le timeout n'est pas efficace
    // useEffect(() => {
    //     if (!socketOpen || !socketRef.current) return;

    //     // Heartbeat : envoie un ping toutes les 30 secondes
    //     const pingInterval = setInterval(() => {
    //         if (socketRef.current?.readyState === WebSocket.OPEN) {
    //             socketRef.current.send(JSON.stringify({ action: 'ping' }));
    //         }
    //     }, 30000);

    //     // Surveillance : vérifie l'état du socket toutes les 5 secondes
    //     const checkInterval = setInterval(() => {
    //         if (socketRef.current?.readyState === WebSocket.CLOSED) {
    //             createSocket();
    //         }
    //     }, 5000);

    //     return () => {
    //         clearInterval(pingInterval);
    //         clearInterval(checkInterval);
    //     };
    // }, [socketOpen, createSocket]);

    useSessionTimeout(kc, () => {});

    const contextValue = useMemo(
        () => ({
            baseWebsocketUrl,
            createSocket,
            sendOnSocket,
            getSocket,
            hasSocket,
            socketOpen,
            messagesByTopic,
            unreadCountByTopic,
            isConnectedByUserIdentifier,
            fetchMessages,
            sendMessage,
            updateMessage,
            deleteMessage,
            getMessageCount,
            setMessagesRead,
            getIsConnected,
            getError: () => errors,
        }),
        [
            baseWebsocketUrl,
            createSocket,
            sendOnSocket,
            getSocket,
            hasSocket,
            socketOpen,
            messagesByTopic,
            unreadCountByTopic,
            isConnectedByUserIdentifier,
            fetchMessages,
            sendMessage,
            updateMessage,
            deleteMessage,
            getMessageCount,
            setMessagesRead,
            getIsConnected,
            errors,
        ],
    );

    return <WebsocketContext.Provider value={contextValue}>{children}</WebsocketContext.Provider>;
};
